import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryStates } from 'nuqs';
import {
  NotesRecordIdSchema,
  NotesThreadResponseSchema,
  noteTargetKey,
  type NoteTarget,
} from '@shared/flow/notes';
import { flowNotesParams, FLOW_NOTES_URL_PARAM } from '../../../../lib/urlParams';
import { useApi } from '../../../../lib/api';
import { fetchJson } from '../../../../lib/apiFetchJson';
import { useNotesEnabled } from '../../../../hooks/useNotesEnabled';
import { useEnrollQueue } from '../../EnrollQueue/EnrollQueueContext';
import { useFlowNotesStore } from './flow-notes-store';
import { notesThreadKey } from './useFlowNotes';
import { useOpenNotes } from './useOpenNotes';

/**
 * Deep link `?notes=a:<sfId>` / `?notes=c:<sfId>` <-> the dock's Notes target
 * (U9c). Mounted by `RailDock` beside `useDockUrlSync`, after the same shape
 * as its `?request=` pair, but as its own hook so it sits behind the Notes
 * flag. nuqs ONLY: react-router's search-params setter rebuilds the query string
 * from a snapshot that cannot see nuqs-owned params (see `urlParams.ts`).
 *
 * - READ: a valid `a:` opens the account thread; a valid `c:` fetches the
 *   thread to learn `accountId` first, then opens. A malformed value is
 *   removed (flag on only). Flag off: the URL is left completely alone.
 * - WRITE: the param mirrors the target while the dock is open on Notes and
 *   is cleared otherwise. It stands down while an incoming value is still
 *   being applied (`pendingKeyRef`), so initialization never wipes the link.
 * - A contact lookup is superseded by any later URL change and abandoned if
 *   the dock is closed, the tab changes, or the hook unmounts while it is in flight.
 */
export function useNotesUrlSync(): void {
  const { enabled } = useNotesEnabled();
  const { isOpen, activeTab } = useEnrollQueue();
  const target = useFlowNotesStore(s => s.target);
  const openNotes = useOpenNotes();
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [{ [FLOW_NOTES_URL_PARAM]: urlNotes }, setParams] = useQueryStates(flowNotesParams, { history: 'replace' });

  const openNotesRef = useRef(openNotes);
  openNotesRef.current = openNotes;
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  const visibleKeyRef = useRef<string | null>(null);
  visibleKeyRef.current = isOpen && activeTab === 'notes' && target ? noteTargetKey(target) : null;

  /** The URL value being applied to the dock; WRITE stands down until the dock shows it. */
  const pendingKeyRef = useRef<string | null>(null);
  /** Bumped by every READ run and every abandonment; a lookup acts only on its own generation. */
  const generationRef = useRef(0);
  const lookupInFlightRef = useRef(false);

  // READ — URL -> dock.
  useEffect(() => {
    // StrictMode: lifetime state is reset at the top of the (re)mount effect.
    generationRef.current += 1;
    const generation = generationRef.current;
    lookupInFlightRef.current = false;
    pendingKeyRef.current = null;
    if (!enabled || !urlNotes) return;
    // Our own WRITE echoed back, or the dock already shows it: nothing to do.
    if (urlNotes === visibleKeyRef.current) return;

    const kind = urlNotes.startsWith('a:') ? 'account' : urlNotes.startsWith('c:') ? 'contact' : null;
    const id = urlNotes.slice(2);
    if (!kind || !NotesRecordIdSchema.safeParse(id).success) {
      void setParamsRef.current({ [FLOW_NOTES_URL_PARAM]: null });
      return;
    }

    pendingKeyRef.current = urlNotes;
    if (kind === 'account') {
      openNotesRef.current({ kind, id, accountId: id }, { entryPoint: 'deep_link' });
      return;
    }

    lookupInFlightRef.current = true;
    const lookupKey = notesThreadKey({ kind, id, accountId: null });
    const params = new URLSearchParams({ kind, id });
    queryClient
      .fetchQuery({
        queryKey: lookupKey,
        queryFn: () => fetchJson(apiFetchRef.current, NotesThreadResponseSchema, `/api/flow/notes/thread?${params.toString()}`),
      })
      .then(
        response => {
          if (generation !== generationRef.current) return;
          lookupInFlightRef.current = false;
          const resolved: NoteTarget = { kind, id, accountId: response.target.accountId };
          queryClient.setQueryData(notesThreadKey(resolved), response);
          openNotesRef.current(resolved, { entryPoint: 'deep_link' });
        },
        () => {
          if (generation !== generationRef.current) return;
          // Unresolvable contact: drop the link rather than leave a lie in the address bar.
          lookupInFlightRef.current = false;
          pendingKeyRef.current = null;
          void setParamsRef.current({ [FLOW_NOTES_URL_PARAM]: null });
        },
      );
    // Unmount (or a superseding run) invalidates this run's continuation.
    return () => {
      generationRef.current += 1;
    };
  }, [enabled, urlNotes, queryClient]);

  // Abandon a pending lookup when the rep closes the dock, leaves the tab, or
  // picks a different record locally (their selection beats a late response).
  const prevIsOpenRef = useRef(isOpen);
  const prevTabRef = useRef(activeTab);
  const targetKey = target ? noteTargetKey(target) : null;
  const prevTargetKeyRef = useRef(targetKey);
  useEffect(() => {
    const closed = prevIsOpenRef.current && !isOpen;
    const tabChanged = prevTabRef.current !== activeTab;
    const pickedElsewhere =
      prevTargetKeyRef.current !== targetKey && targetKey !== null && targetKey !== pendingKeyRef.current;
    prevIsOpenRef.current = isOpen;
    prevTabRef.current = activeTab;
    prevTargetKeyRef.current = targetKey;
    if (lookupInFlightRef.current && (closed || tabChanged || pickedElsewhere)) {
      generationRef.current += 1;
      lookupInFlightRef.current = false;
      pendingKeyRef.current = null;
    }
  }, [isOpen, activeTab, targetKey]);

  // WRITE — dock -> URL.
  useEffect(() => {
    if (!enabled) return;
    const key = visibleKeyRef.current;
    if (pendingKeyRef.current !== null) {
      if (key !== pendingKeyRef.current) return;
      pendingKeyRef.current = null;
    }
    if (key === urlNotes) return;
    void setParams({ [FLOW_NOTES_URL_PARAM]: key });
  }, [enabled, isOpen, activeTab, target, urlNotes, setParams]);
}
